import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { useUser } from "../../user/hooks/useUser"
import { modals } from "@mantine/modals"
import { useDocument, useDeleteDocument } from "../hooks/useDocuments"
import DocumentDetailContent from "./DocumentDetailContent"
import DocumentFormContent from "./DocumentFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedDocId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function DocumentDrawer({ opened, onClose, mode, selectedDocId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: doc, isLoading } = useDocument(mode !== "create" ? selectedDocId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageDocuments ?? false
  const deleteMutation = useDeleteDocument()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("documents.confirmDelete"),
      children: <Text size="sm">{t("documents.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(doc!.id, {
          onSuccess: () => {
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("documents.createTitle")
      : mode === "edit"
        ? t("documents.editTitle")
        : doc?.real_name ?? ""

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <DocumentFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !doc ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <DocumentFormContent ref={formRef} doc={doc} onSuccess={onClose} />
      ) : (
        <DocumentDetailContent
          doc={doc}
        />
      )}
    </Drawer>
  )
}
